import React from "react";
import { cn } from "@/lib/utils";

type PolymorphicProps<C extends React.ElementType> = {
  as?: C;
} & React.ComponentPropsWithRef<C>;

export function Box<C extends React.ElementType = "div">({
  as: Comp = "div",
  className,
  ref,
  ...props
}: PolymorphicProps<C>) {
  return <Comp ref={ref} className={className} {...props} />;
}
Box.displayName = "Box";

export function ScrollX<T extends React.ElementType = "div">({
  as: Comp = "div",
  className,
  ...props
}: PolymorphicProps<T>) {
  return <Comp className={cn("scroll-x", className)} {...props} />;
}
ScrollX.displayName = "ScrollX";

export function ScrollY<T extends React.ElementType = "div">({
  as: Comp = "div",
  className,
  ...props
}: PolymorphicProps<T>) {
  return <Comp className={cn("scroll-y", className)} {...props} />;
}

export function ScrollBoth<T extends React.ElementType = "div">({
  as: Comp = "div",
  className,
  ...props
}: PolymorphicProps<T>) {
  return <Comp className={cn("scroll-both", className)} {...props} />;
}

export function Flex<T extends React.ElementType = "div">({
  as: Comp = "div",
  direction = "row",
  wrap = false,
  align,
  justify,
  gap,
  className,
  ...props
}: PolymorphicProps<T> & {
  direction?: "row" | "col";
  wrap?: boolean;
  align?: "start" | "center" | "end" | "stretch";
  justify?: "start" | "center" | "end" | "between" | "around";
  gap?: string;
}) {
  const classes = cn(
    "flex",
    direction === "col" ? "flex-col" : "flex-row",
    wrap && "flex-wrap",
    align && `items-${align}`,
    justify && `justify-${justify}`,
    gap && `gap-${gap}`,
    className
  );
  return <Comp className={classes} {...props} />;
}
Flex.displayName = "Flex";

export function FlexItem<T extends React.ElementType = "div">({
  as: Comp = "div",
  grow = false,
  shrink = false,
  basis,
  className,
  ...props
}: PolymorphicProps<T> & {
  grow?: boolean;
  shrink?: boolean;
  basis?: string;
}) {
  const classes = cn(
    grow && "grow",
    shrink && "shrink min-w-0 max-w-full",
    basis && `basis-[${basis}]`,
    className
  );
  return <Comp className={classes} {...props} />;
}
FlexItem.displayName = "FlexItem";

export function HStack(props: React.ComponentProps<typeof Flex>) {
  return <Flex direction="row" {...props} />;
}
HStack.displayName = "HStack";

export function VStack(props: React.ComponentProps<typeof Flex>) {
  return <Flex direction="col" {...props} />;
}
VStack.displayName = "VStack";

export function Text<T extends React.ElementType = "span">({
  as: Comp = "span",
  truncate = false,
  className,
  ...props
}: PolymorphicProps<T> & { truncate?: boolean } & Omit<
    React.ComponentPropsWithoutRef<T>,
    "as"
  >) {
  return (
    <Comp className={cn(truncate && "truncate block", className)} {...props} />
  );
}
Text.displayName = "Text";
